import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FolderOpen, Sparkles, Layers, MonitorSmartphone } from 'lucide-react';
interface SlideWhereWeFitProps {
  onContinue: () => void;
}
type Accent = 'sky' | 'teal' | 'purple';
const COLUMNS: {
  step: string;
  owner: string;
  title: string;
  icon: React.ReactNode;
  accent: Accent;
  items: string[];
}[] = [{
  step: '01',
  owner: 'LEO — creative & production',
  title: 'Existing approved content',
  icon: <FolderOpen className="w-5 h-5" data-id="element-1496" data-mp="3ut0p8bcvu8tq" />,
  accent: 'sky',
  items: ['Campaign strategy & scientific narrative', 'Core claims and brand standards', 'Master assets, decks, key visuals', 'Global MLR approval']
}, {
  step: '02',
  owner: 'Snakker',
  title: 'Snakkerfy it',
  icon: <Sparkles className="w-5 h-5" data-id="element-1497" data-mp="1e4ersp9vkr4d" />,
  accent: 'teal',
  items: ['Master Snak built from your approved content', 'Reframed for short, relevant HCP engagement', 'Tightened into an interactive Snak format', 'Nothing invented — claims stay intact']
}, {
  step: '03',
  owner: 'Snakker',
  title: 'Localize · publish · learn',
  icon: <Layers className="w-5 h-5" data-id="element-1498" data-mp="2chg2ryldlak7" />,
  accent: 'teal',
  items: ['Localization inside the Snak platform', 'Campaign logic per market and audience', 'Live execution without affiliate hours', 'Insight capture and optimization loop']
}, {
  step: '04',
  owner: 'LEO — production team',
  title: 'LEO-owned channels',
  icon: <MonitorSmartphone className="w-5 h-5" data-id="element-1499" data-mp="1evs4171g6g5i" />,
  accent: 'purple',
  items: ['Website publishing', 'Email and CRM deployment', 'IVA and rep-facing channels', 'Owned-channel governance']
}];
const DOT: Record<Accent, string> = {
  sky: 'bg-sky-400',
  teal: 'bg-teal-400',
  purple: 'bg-purple-400'
};
const CHIP: Record<Accent, string> = {
  sky: 'bg-sky-400/10 text-sky-400',
  teal: 'bg-teal-400/15 text-teal-400',
  purple: 'bg-purple-500/10 text-purple-400'
};
export function SlideWhereWeFit({
  onContinue
}: SlideWhereWeFitProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [250, 700, 1150, 1600, 2200].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-16 md:py-12 max-w-[1400px] mx-auto" data-id="element-1500" data-mp="2att8tmymagxt">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-3 shrink-0" data-id="element-1501" data-mp="1j4zj8ymbrrbk">
        <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-1502" data-mp="1rx9w6noluawi">
          Where We Fit
        </p>
        <h2 className="text-3xl md:text-5xl font-serif text-white" data-id="element-1503" data-mp="vfx5w2yq4kmv">
          We start with the content you already have
        </h2>
        <p className="text-lg text-white/60" data-id="element-1504" data-mp="zffogs32ojmj">
          LEO has internalized production. What is missing is the Snakkerfy
          Layer — short, relevant, live in every market.
        </p>
      </motion.div>

      <div className="flex-1 min-h-0 flex flex-col justify-center w-full mt-6 space-y-4" data-id="element-1505" data-mp="mz67uklzdc4s">
        {/* Ownership bands */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: stage >= 1 ? 1 : 0
      }} className="hidden md:grid grid-cols-4 gap-4" data-id="element-1506" data-mp="iymdtpwpeomv">
          <div className="rounded-lg border border-navy-600 px-3 py-2 text-center" data-id="element-1507" data-mp="485b838c1soe">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-white/40" data-id="element-1508" data-mp="3ve6fnsxslo12">
              Stays with LEO
            </p>
          </div>
          <div className="col-span-2 rounded-lg border border-teal-400/40 bg-teal-400/[0.07] px-3 py-2 text-center" data-id="element-1509" data-mp="3rdwdkrd4fhrh">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-teal-400" data-id="element-1510" data-mp="3mcqe8aoygw8z">
              The Snakkerfy Layer
            </p>
          </div>
          <div className="rounded-lg border border-navy-600 px-3 py-2 text-center" data-id="element-1511" data-mp="1lb0ox2a2g8do">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-white/40" data-id="element-1512" data-mp="1qavft5tucxyq">
              Stays with LEO
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1513" data-mp="1bjmrd01odin6">
          {COLUMNS.map((col, idx) => {
          const visible = stage >= idx + 1;
          const isSnakker = col.accent === 'teal';
          return <motion.div key={col.step} initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: visible ? 1 : 0,
            y: visible ? 0 : 16
          }} className={`rounded-2xl border p-5 flex flex-col ${isSnakker ? 'border-teal-400/40 bg-teal-400/[0.07]' : 'border-navy-600 bg-navy-900/50'}`} data-id="element-1514" data-mp="16nfm79h3sb6a" data-mpi={idx}>
                <div className="flex items-center justify-between gap-2 mb-4" data-id="element-1515" data-mp="gcnehmpipmlj">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${CHIP[col.accent]}`} data-id="element-1516" data-mp="beu6co4nmiim">
                    {col.icon}
                  </div>
                  <span className={`text-sm font-semibold ${isSnakker ? 'text-teal-400' : 'text-white/25'}`} data-id="element-1517" data-mp="l9vxtqmejwod">
                    {col.step}
                  </span>
                </div>
                <p className="text-white/40 text-[11px] font-semibold uppercase tracking-widest mb-1.5" data-id="element-1518" data-mp="u64syt3ck94p">
                  {col.owner}
                </p>
                <h3 className="text-white font-semibold text-lg mb-4 leading-snug" data-id="element-1519" data-mp="1439jf5486qrb">
                  {col.title}
                </h3>
                <ul className="space-y-2 flex-1" data-id="element-1520" data-mp="3auiheydcr5o5">
                  {col.items.map(item => <li key={item} className="flex items-start gap-2.5 text-white/75 text-[13px] leading-snug" data-id="element-1521" data-mp="35ve909y95byv">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 ${DOT[col.accent]}`} data-id="element-1522" data-mp="3e69qc3llr1t" />
                      {item}
                    </li>)}
                </ul>
              </motion.div>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 5 ? 1 : 0,
        y: stage >= 5 ? 0 : 12
      }} className="bg-navy-900/50 border border-navy-700 rounded-2xl px-7 py-5" data-id="element-1523" data-mp="iaoo266zwr55">
          <p className="text-white/90 text-base md:text-lg leading-relaxed" data-id="element-1524" data-mp="w1jyhmx57gau">
            We do not replace LEO's production team. We take{' '}
            <span className="text-teal-400 font-semibold" data-id="element-1525" data-mp="1609h5hwv54s5">
              content that already exists and Snakkerfy it
            </span>{' '}
            — same claims, same narrative, reshaped for short, relevant
            engagement — then localize it, get it live and keep improving it.
          </p>
        </motion.div>
      </div>
    </div>;
}